import { MDBAutocomplete, MDBCheckbox, MDBIcon, MDBSelect } from "mdb-react-ui-kit";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { externalNumberearch } from "../apiclients/streamsapi_bridge";
import { unFormatNumber, validatePhoneNumber } from "../views/popups/phonenumbers/AddRemoveNumbers";

/**
 * RouteCalls – Unified, reusable routing destination picker.
 *
 * Props
 * ─────
 * destRecords        : object  – { user:[], hggroup:[], userVoicemail:[], voicemail:{}, … }
 * callerList         : array   – [{ phnum, addl_info }]
 * command            : number  – keypad command (-1 for HG busy/no-answer)
 * fieldNames         : object  – field-path overrides (see DEFAULT_FIELD_NAMES)
 * routeDetails       : object  – pre-populated values from the server
 * executeSetting     : bool    – when true, reset internal state
 * setIsloading       : fn      – parent loading setter
 * mainLabel          : string  – placeholder for the main autocomplete
 * mainRouteError     : string  – validation message for the main field
 * aaFlag             : bool    – enables AA-only options (Directory)
 */

const DEFAULT_FIELD_NAMES = {
  destinationType: "routing.busy_or_no_answer.destination_type",
  destinationvalue: "routing.busy_or_no_answer.value",
  entityType: "routing.busy_or_no_answer.entity_type",
  ph_num: "routing.busy_or_no_answer.phnumber",
  timeout: "routing.busy_or_no_answer.timeout",
  timeoutFlag: "routing.busy_or_no_answer.timeout_flag",
  timeoutrouting: "routing.busy_or_no_answer.timeout_routing",
  timeoutdesttyperouting: "routing.busy_or_no_answer.timeout_dest_type",
  cidPassFlag: "routing.busy_or_no_answer.cid_pass_flag",
  cidPass: "routing.busy_or_no_answer.cid_pass",
};

const MAX_DEFAULT_RESULTS = 10;
const CREATE_EXTERNAL_LABEL = "Add External Number";

// ACD-specific type → category label map (mirrors acdcatagerory in BusyNoAnswerRoute)
const ACD_TYPE_CATEGORY = {
  10: "Hunt Group-Voicemail",
  3: "Call Queue-Voicemail",
  16: "Auto-Attendant-Voicemail",
  9: "User-Voicemail",
  4: "User",
  5: "Hunt Group",
  11: "Auto-Attendant",
  1: "Call Queue",
};

// ─── helpers ────────────────────────────────────────────────────────────────

/**
 * The fetch layer (fetchAAMiscData / fetchRoutingMiscData) strips the
 * "-Voicemail" suffix from category before storing items in userVoicemail:
 *   item.category.replace(/-Voicemail$/, "")
 *
 * So a voicemail item arrives with category = "Hunt Group" / "User" /
 * "Auto-Attendant" / "Call Queue" — NOT the full "Hunt Group-Voicemail" etc.
 *
 * tagVoicemailItem re-attaches the suffix via _displayCategory for display
 * purposes and marks the item with _isVoicemail=true so resolveDestinationType
 * and resolveEntityType can handle it correctly regardless of the stripped value.
 */
function tagVoicemailItem(item) {
  return {
    ...item,
    _isVoicemail: true,
    // Full display category: "Hunt Group-Voicemail", "User-Voicemail", etc.
    _displayCategory: item.category ? `${item.category}-Voicemail` : "",
  };
}

/**
 * Build the unified selectable list.
 * - For HG routing (aaFlag=false): user + hggroup + userVoicemail
 * - For AA routing (aaFlag=true) : user + hggroup + aagroup + userVoicemail
 *   (mirrors BusyNoAnswerRoute displayData which includes aagroup for AA)
 */
function buildUnifiedList(destRecords, aaFlag) {
  if (!destRecords || typeof destRecords !== "object") return [];
  const {
    user = [],
    hggroup = [],
    userVoicemail = [],
    aagroup = [],
    operators = {},
    dialByName = {},
  } = destRecords;

  const base = [...user, ...hggroup];

  base.push(...userVoicemail.map(tagVoicemailItem));

  // AA-only: also include aagroup entries
  if (aaFlag) {
    base.push(...aagroup);
  }

  // Sort by extension number ascending; items without an extension fall to the end (Infinity)
  return base.sort((a, b) => (a?.extensions ?? Infinity) - (b?.extensions ?? Infinity));
}

/**
 * Inner timeout-routing list excludes External Numbers, Operator, Directory
 * (same as OPERATOR_INNER_TYPES in BusyNoAnswerRoute).
 */
function buildInnerList(destRecords) {
  if (!destRecords || typeof destRecords !== "object") return [];
  const { user = [], hggroup = [], userVoicemail = [] } = destRecords;
  return [...user, ...hggroup, ...userVoicemail.map(tagVoicemailItem)].sort((a, b) =>
    (a?.extensions ?? Infinity) - (b?.extensions ?? Infinity)
  );
}

function itemLabel(item) {
  if (!item) return "";
  const ext = item.extensions ? `${item.extensions} : ` : "";
  // For voicemail items use _displayCategory (e.g. "Hunt Group-Voicemail") since
  // the fetch layer strips the "-Voicemail" suffix before storing in userVoicemail.
  const rawCat = item._isVoicemail ? (item._displayCategory ?? "") : (item.category ?? "");
  const cat = rawCat ? ` : ${rawCat}` : "";
  return `${ext}${item.title}${cat}`;
}

function resolveDestinationType(item, acdFlag = false) {
  // Voicemail items (_isVoicemail set by tagVoicemailItem)
  if (item?._isVoicemail) {
    if (!acdFlag) return 6;
    // ACD voicemail types mirror getDestinationType in OperatorSelect
    switch (item.category) {
      case "Hunt Group": return 10;
      case "Call Queue": return 3;
      case "User": return 9;
      case "Auto-Attendant": return 16;
      default: return 6;
    }
  }

  const cat = item?.category ?? "";

  if (!acdFlag) {
    const map = {
      "Hunt Group": 0,
      "ACD Group": 0,
      "Auto-Attendant": 0,
      User: 1,
      "Hunt Group-Voicemail": 6,
      "Auto-Attendant-Voicemail": 6,
      "User-Voicemail": 6,
      "Call Queue-Voicemail": 6,
    };
    return map[cat] ?? 0;
  }

  // ACD-specific destination types (mirrors getDestinationType in OperatorSelect)
  switch (cat) {
    case "User": return 4;
    case "Hunt Group": return 5;
    case "Auto-Attendant": return 11;
    case "Call Queue": return 1;
    default: return 0;
  }
}

function resolveEntityType(item) {
  // For voicemail items flagged by tagVoicemailItem, item.category is the
  // STRIPPED value ("Hunt Group", "User", etc.) — use _isVoicemail to detect
  // them and map by the stripped category.
  if (item?._isVoicemail) {
    if (item.category === "User") return 1;
    return 2; // Hunt Group, Auto-Attendant, Call Queue voicemails
  }

  // Keep original checks for any non-tagged items that may still carry the
  // full category string (e.g. items returned by DID search).
  const cat = item?.category ?? "";
  if (["Hunt Group-Voicemail", "Auto-Attendant-Voicemail", "Call Queue-Voicemail"].includes(cat))
    return 2;
  if (cat === "User-Voicemail") return 1;
  return undefined;
}

function getNestedError(errors, path) {
  if (!path) return undefined;
  const normalised = path.replace(/\[(\d+)\]/g, ".$1");
  return normalised
    .split(".")
    .reduce(
      (acc, k) => (acc && acc[k] !== undefined ? acc[k] : undefined),
      errors
    )?.message;
}

// ─── component ──────────────────────────────────────────────────────────────

const RouteCalls = ({
  destRecords = {},
  callerList = [],
  command = -1,
  fieldNames = DEFAULT_FIELD_NAMES,
  routeDetails = {},
  executeSetting = false,
  setIsloading,
  mainLabel = "Search destination or create external number",
  mainRouteError = "Please select a destination",
  aaFlag = false,
  acdFlag = false,
}) => {
  const { t } = useTranslation();
  const mergedFields = useMemo(
    () => ({ ...DEFAULT_FIELD_NAMES, ...fieldNames }),
    [fieldNames]
  );

  const {
    register,
    control,
    setValue,
    watch,
    setError,
    clearErrors,
    unregister,
    formState: { errors },
  } = useFormContext();

  // ── unified list (built once when destRecords / aaFlag changes) ─
  const fullList = useMemo(
    () => buildUnifiedList(destRecords, aaFlag),
    [destRecords, aaFlag]
  );

  // Inner list (timeout routing) excludes AA-only / external types
  const innerList = useMemo(
    () => buildInnerList(destRecords),
    [destRecords]
  );

  // ── local display state ──────────────────────────────────────────────────
  const [displayedOptions, setDisplayedOptions] = useState([]);
  const [mainValue, setMainValue] = useState(""); // text shown in main autocomplete
  const [isExternalSelected, setIsExternalSelected] = useState(false);
  const [externalNumber, setExternalNumber] = useState("");
  const [didSearchError, setDidSearchError] = useState("");
  // Ref mirrors didSearchError so the validate function always reads the latest
  // value even when react-hook-form calls it with a stale closure on submit.
  const didSearchErrorRef = useRef("");

  // caller-id dropdown
  const [callerIdList, setCallerIdList] = useState([]);
  const [cidPassValue, setCidPassValue] = useState("-1");

  const typingTimerRef = useRef(null);
  const displayedOptionsRef = useRef([]);

  // ── seed default display list ─────────────────────────────────────────────
  const buildDefaultDisplay = useCallback(() => {
    // AA-only: add Directory option (matches BusyNoAnswerRoute aaFlag check)
    const extras = aaFlag
      ? [{ title: "Directory", _isDirectory: true, _label: "Directory" }]
      : [];

    return [
      { title: CREATE_EXTERNAL_LABEL, _isExternal: true, _label: CREATE_EXTERNAL_LABEL },
      ...extras,
    ];
  }, [aaFlag]);

  useEffect(() => {
    setDisplayedOptions(buildDefaultDisplay());
  }, [buildDefaultDisplay]);

  // Keep ref in sync so onSelect can read the latest displayedOptions
  // without adding it to useCallback deps (avoids re-creating onSelect on every search)
  useEffect(() => {
    displayedOptionsRef.current = displayedOptions;
  }, [displayedOptions]);

  // ── caller list ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (callerList.length > 0) {
      setCallerIdList(
        callerList.map((c) => ({ text: c.addl_info, value: c.phnum }))
      );
    }
  }, [callerList]);

  // ── reset when executeSetting fires ──────────────────────────────────────
  useEffect(() => {
    if (executeSetting) {
      setMainValue("");
      setIsExternalSelected(false);
      setExternalNumber("");
      setCidPassValue("-1");
      didSearchErrorRef.current = "";
      setDidSearchError("");
      setDisplayedOptions(buildDefaultDisplay());
    }
  }, [executeSetting, buildDefaultDisplay]);

  // ── pre-populate from routeDetails ───────────────────────────────────────
  useEffect(() => {
    if (!routeDetails || Object.keys(routeDetails).length === 0) return;

    // ACD uses 'type' field; HG/AA uses 'destination_type'
    const rawType = routeDetails.destination_type
      ?? (acdFlag ? routeDetails.type : undefined);
    const destType = parseInt(rawType ?? -1);

    if ((!acdFlag && destType === 4) || (acdFlag && destType === 12)) {
      // external number (type=4 for HG/AA, type=12 for ACD)
      setIsExternalSelected(true);
      setMainValue(CREATE_EXTERNAL_LABEL);
      const ph = routeDetails.phnumber ?? routeDetails.title ?? "";
      setExternalNumber(ph);
      setCidPassValue(routeDetails.cid_pass ?? "-1");
    } else if (destType === 13) {
      // Directory (AA Routing only)
      setIsExternalSelected(false);
      setMainValue("Directory");
    } else if (destType !== -1) {
      // internal route – derive category label
      // ACD data may have no 'category' field; derive from ACD_TYPE_CATEGORY map
      let catLabel = routeDetails.category ?? "";
      if (!catLabel && acdFlag && ACD_TYPE_CATEGORY[destType]) {
        catLabel = ACD_TYPE_CATEGORY[destType];
      }
      const ext = routeDetails.extension ? `${routeDetails.extension} : ` : "";
      const cat = catLabel ? ` : ${catLabel}` : "";
      const label = `${ext}${routeDetails.title ?? ""}${cat}`;
      setIsExternalSelected(false);
      setMainValue(label);
    }
  }, [routeDetails, acdFlag]);

  // ── search ────────────────────────────────────────────────────────────────
  const onSearch = useCallback(
    (raw) => {
      const term = (raw ?? "")
        .toLowerCase()
        .trim();

      if (!term) {
        setDisplayedOptions(buildDefaultDisplay());
        return;
      }

      const matched = fullList
        .filter((item) => {
          const titleMatch = item.title?.toLowerCase().includes(term);
          const extMatch = item.extensions?.toString().includes(term);
          return titleMatch || extMatch;
        })
        .slice(0, MAX_DEFAULT_RESULTS)
        .map((item) => ({ ...item, _label: itemLabel(item) }));

      const extras = aaFlag
        ? [{ title: "Directory", _isDirectory: true, _label: "Directory" }]
        : [];

      const results = [
        {
          title: CREATE_EXTERNAL_LABEL,
          _isExternal: true,
          _label: CREATE_EXTERNAL_LABEL,
        },
        ...extras,
        ...matched,
      ];

      // DID lookup if looks like a phone number
      const digits = term.replace(/\D/g, "");
      if (digits.length >= 10 && digits.length <= 11) {
        clearTimeout(typingTimerRef.current);
        typingTimerRef.current = setTimeout(() => {
          searchDID(digits, false);
        }, 500);
      }

      setDisplayedOptions(results);
    },
    [fullList, buildDefaultDisplay, aaFlag]
  );

  // ── DID API search ────────────────────────────────────────────────────────
  const searchDID = async (number, isExternalInput) => {
    try {
      if (setIsloading) setIsloading(true);
      const siteid = JSON.parse(
        sessionStorage.getItem("admin_session")
      ).siteid;
      const resp = await externalNumberearch(siteid, number);
      if (setIsloading) setIsloading(false);
      const data = await resp.json();

      if (data.status === "success") {
        if (data.routing_obj) {
          const linked = data.routing_obj;
          if (isExternalInput) {
            // External input: show error – already linked to something
            const name = linked.title ?? linked.name ?? "another entity";
            const linkedMsg = `This is an internal number. Please provide an external number.`;
            didSearchErrorRef.current = linkedMsg;
            setDidSearchError(linkedMsg);
            setError(mergedFields.ph_num, {
              type: "server",
              message: linkedMsg,
            });
          } else {
            // Main-search: surface it as a selectable dropdown option
            clearErrors(mergedFields.destinationvalue);
            const linkedItem = {
              ...linked,
              title: linked.title ?? linked.name ?? number,
              extensions:
                linked.extensions ?? linked.extension ?? number,
              category: linked.category ?? "",
              _label: itemLabel({
                title: linked.title ?? linked.name ?? number,
                extensions:
                  linked.extensions ?? linked.extension ?? number,
                category: linked.category ?? "",
              }),
              _fromDID: true,
            };
            setDisplayedOptions((prev) => {
              const withoutDID = prev.filter((o) => !o._fromDID);
              return [withoutDID[0], linkedItem, ...withoutDID.slice(1)];
            });
          }
        } else {
          // DID not found in system
          if (isExternalInput) {
            clearErrors(mergedFields.ph_num);
            didSearchErrorRef.current = "";
            setDidSearchError("");
          } else {
            clearErrors(mergedFields.destinationvalue);
          }
        }
      } else {
        const msgs = data.message?.errors ?? [];
        msgs.forEach((err) => {
          if (isExternalInput) {
            didSearchErrorRef.current = err.message;
            setDidSearchError(err.message);
            setError(mergedFields.ph_num, {
              type: "server",
              message: err.message,
            });
          } else {
            setError(mergedFields.destinationvalue, {
              type: "server",
              message: err.message,
            });
          }
        });
      }
    } catch (e) {
      if (setIsloading) setIsloading(false);
      console.error("RouteCalls DID search error:", e);
    }
  };

  // ── handle selection from autocomplete ───────────────────────────────────
  const onSelect = useCallback(
    (selectedLabel) => {
      if (!selectedLabel) return;

      // "Create External Number" chosen
      if (selectedLabel === CREATE_EXTERNAL_LABEL) {
        setIsExternalSelected(true);
        setMainValue(CREATE_EXTERNAL_LABEL);
        setExternalNumber("");
        didSearchErrorRef.current = "";
        setDidSearchError("");
        clearErrors(mergedFields.destinationvalue);
        clearErrors(mergedFields.ph_num);
        // ACD uses type=12; HG/AA uses type=4
        setValue(mergedFields.destinationType, acdFlag ? 12 : 4, { shouldDirty: true });
        setValue(mergedFields.destinationvalue, "", { shouldDirty: true });
        return;
      }

      // AA-only: "Directory" chosen (mirrors BusyNoAnswerRoute handleTypeClick for Directory)
      if (selectedLabel === "Directory") {
        setIsExternalSelected(false);
        setMainValue("Directory");
        clearErrors(mergedFields.destinationvalue);
        setValue(mergedFields.destinationType, 13, { shouldDirty: true });
        setValue(mergedFields.destinationvalue, "0", { shouldDirty: true });
        // Reset external-related fields
        resetExternalFields();
        return;
      }

      // Internal item selected
      setIsExternalSelected(false);
      setMainValue(selectedLabel);
      clearErrors(mergedFields.destinationvalue);
      clearErrors(mergedFields.ph_num);
      didSearchErrorRef.current = "";
      setDidSearchError("");

      // First look in the unified list; fall back to DID search results
      // which are only present in displayedOptions (not fullList)
      let item = fullList.find((i) => itemLabel(i) === selectedLabel);
      if (!item) {
        item = displayedOptionsRef.current.find(
          (i) => i._fromDID && (i._label === selectedLabel || itemLabel(i) === selectedLabel)
        );
      }
      if (item) {
        const destType = resolveDestinationType(item, acdFlag);
        const entityType = resolveEntityType(item);
        const value = item.agentid ?? item.code ?? "";

        setValue(mergedFields.destinationType, destType, {
          shouldDirty: true,
        });
        setValue(mergedFields.destinationvalue, String(value), {
          shouldDirty: true,
        });
        if (mergedFields.entityType && entityType !== undefined) {
          setValue(mergedFields.entityType, entityType, {
            shouldDirty: true,
          });
        }
      }

      resetExternalFields();
    },
    [fullList, mergedFields, clearErrors, setValue, acdFlag]
  );

  // Helper: reset all external-number–related form fields
  const resetExternalFields = useCallback(() => {
    setValue(mergedFields.ph_num, "", { shouldDirty: false });
    setValue(mergedFields.timeoutFlag, false, { shouldDirty: false });
    setValue(mergedFields.timeout, 0, { shouldDirty: false });
    setValue(mergedFields.timeoutrouting, "", { shouldDirty: false });
    setValue(mergedFields.timeoutdesttyperouting, -1, { shouldDirty: false });
    setValue(mergedFields.cidPassFlag, true, { shouldDirty: false });
    setValue(mergedFields.cidPass, "", { shouldDirty: false });
  }, [mergedFields, setValue]);

  // ── external number input change ──────────────────────────────────────────
  const onExternalNumberChange = (e) => {
    const raw = e.target.value.trim();
    setExternalNumber(raw);
    clearErrors(mergedFields.ph_num);
    didSearchErrorRef.current = "";
    setDidSearchError("");

    // Keep phnumber field in sync (used by HGSettings / AccInfo on submit)
    setValue(mergedFields.ph_num, raw, { shouldDirty: true });
    // Also keep destinationvalue in sync
    setValue(mergedFields.destinationvalue, raw, { shouldDirty: true });

    const digits = raw.replace(/\D/g, "");
    if (digits.length >= 10 && digits.length <= 11) {
      clearTimeout(typingTimerRef.current);
      typingTimerRef.current = setTimeout(() => {
        searchDID(digits, true);
      }, 500);
    }
  };

  // ── timeout checkbox ──────────────────────────────────────────────────────
  const timeoutFlagValue = watch(mergedFields.timeoutFlag);
  const cidPassFlagValue = watch(mergedFields.cidPassFlag);

  const onTimeoutFlagChange = (e) => {
    const checked = e.target.checked;
    setValue(mergedFields.timeoutFlag, checked, { shouldDirty: true });
    if (!checked) {
      clearErrors(mergedFields.timeout);
      clearErrors(mergedFields.timeoutrouting);
      clearErrors(mergedFields.timeoutdesttyperouting);
      setValue(mergedFields.timeout, 0, { shouldDirty: false });
      setValue(mergedFields.timeoutrouting, "", { shouldDirty: false });
      setValue(mergedFields.timeoutdesttyperouting, -1, {
        shouldDirty: false,
      });
    }
  };

  // ── caller id ─────────────────────────────────────────────────────────────
  const onCidPassFlagChange = (e) => {
    setValue(mergedFields.cidPassFlag, e.target.checked, {
      shouldDirty: true,
    });
    if (e.target.checked) {
      clearErrors(mergedFields.cidPass);
    }
  };

  const onCidPassChange = (e) => {
    if (e && e.value && e.value !== cidPassValue && e.value !== -1) {
      clearErrors(mergedFields.cidPass);
      setCidPassValue(e.value);
      setValue(mergedFields.cidPass, e.value, { shouldDirty: true });
    }
  };

  // ── cleanup timer on unmount ──────────────────────────────────────────────
  useEffect(() => {
    return () => clearTimeout(typingTimerRef.current);
  }, []);

  // ── display value fn for MDBAutocomplete ─────────────────────────────────
  const displayValue = (item) => {
    if (item._isExternal || item._isDirectory) return item.title;
    return item._label ?? itemLabel(item);
  };

  // ─── render ───────────────────────────────────────────────────────────────
  return (
    <div className="d-flex w-100">
      <div className="phoneselect w-100">

        {/* ── Main destination autocomplete with validation ── */}
        <Controller
          name={mergedFields.destinationvalue}
          control={control}
          rules={{
            validate: (value) => {
              // AA rows that have never been touched must not be validated.
              // An untouched AA row has destination_type = -1 and no mainValue.
              if (aaFlag && !mainValue && !isExternalSelected) return true;

              // If external number is selected, the ph_num Controller handles
              // its own validation – skip here.
              if (isExternalSelected) return true;

              // For non-external selections a destination must have been chosen.
              if (!mainValue || mainValue === "") return mainRouteError;
              return true;
            },
          }}
          render={() => (
            <>
              <div className="position-relative w-100 routing-input">
                <MDBAutocomplete 
                className="pr-4"
                  placeholder={mainLabel}
                  data={displayedOptions}
                  value={mainValue}
                  title={mainValue}
                  displayValue={displayValue}
                  onChange={(val) => {
                    setMainValue(val);
                    if (val === "") {
                      clearErrors(mergedFields.destinationvalue);
                      clearErrors(mergedFields.ph_num);
                      setIsExternalSelected(false);
                      setExternalNumber("");
                      didSearchErrorRef.current = "";
                      setDidSearchError("");
                      setDisplayedOptions(buildDefaultDisplay());
                    }
                  }}
                  onSelect={onSelect}
                  onSearch={onSearch}
                  onClick={(e) =>e.target.select()}
                  itemContent={(item) => (
                    <div className="d-flex justify-content-between align-items-center w-100">
                      <span>{displayValue(item)}</span>
                      {item._isExternal && 
                          <MDBIcon fas icon="plus" size="md" color="primary" /> 
                      }
                    </div>
                  )}
                />
                {/* {mainValue && (
                <div className="routing-clear-icon" onClick={() => {
                  setMainValue("");
                  clearErrors(mergedFields.destinationvalue);
                  clearErrors(mergedFields.ph_num);
                  setIsExternalSelected(false);
                  setExternalNumber("");
                  didSearchErrorRef.current = "";
                  setDidSearchError("");
                  setDisplayedOptions(buildDefaultDisplay());
                }}>
                <MDBIcon
                  fas
                  icon="times" />
                  </div>
                )} */}
              </div>
              {getNestedError(errors, mergedFields.destinationvalue) && (
                <p className="text-danger">
                  {getNestedError(errors, mergedFields.destinationvalue)}
                </p>
              )}
            </>
          )}
        />

        {/* ── External Number section (shown only when "Create External Number" chosen) ── */}
        {isExternalSelected && (
          <>
            {/* External number text input – validated exactly like BusyNoAnswerRoute */}
            <div className="d-flex w-100 mt-2">
              <div className="phoneselect w-100">
                <Controller
                  name={mergedFields.ph_num}
                  control={control}
                  rules={{
                    validate: (value) => {
                      // Only validate when the external section is actually shown
                      if (!isExternalSelected) return true;
                      if (!value || value.trim() === "")
                        return "External Number is required";
                      // Block submit when a DID server error exists (e.g. number
                      // already linked to another entity).  The ref is used
                      // instead of state to avoid stale-closure issues when
                      // react-hook-form re-runs validate during handleSubmit.
                      if (didSearchErrorRef.current) return didSearchErrorRef.current;
                      return validatePhoneNumber(value);
                    },
                  }}
                  render={({ field }) => (
                    <>
                      <input
                        {...field}
                        autoFocus
                        type="text"
                        className="w-100 form-control"
                        placeholder="Enter External Number (10-11 digits)"
                        value={externalNumber}
                        onKeyPress={(e) => {
                          if (!/[0-9+\s-]/.test(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        onChange={(e) => {
                          field.onChange(e);
                          onExternalNumberChange(e);
                        }}
                      />
                      {(getNestedError(errors, mergedFields.ph_num) ||
                        didSearchError) && (
                          <p className="text-danger">
                            {getNestedError(errors, mergedFields.ph_num) ||
                              didSearchError}
                          </p>
                        )}
                    </>
                  )}
                />
              </div>
            </div>

            {/* ── Timeout + Route on Timeout ── */}
            <div className="mt-2 d-flex align-items-center justify-content-between w-100">
              <div className="tel-numbers fw-bold">{t("Timeout")}</div>
              <MDBCheckbox
                name={`timeoutFlag_${command}`}
                id={`timeoutFlag_${command}`}
                checked={!!timeoutFlagValue}
                aria-label="Timeout flag"
                onChange={onTimeoutFlagChange}
              />
            </div>

            <div className="d-flex w-100">
              <div className="phoneselect w-100">
                <input
                  type="text"
                  placeholder="Timeout in seconds"
                  disabled={!timeoutFlagValue}
                  className="w-100 form-control"
                  {...register(mergedFields.timeout, {
                    valueAsNumber: true,
                    validate: (val) => {
                      if (!isExternalSelected) return true;
                      if (!timeoutFlagValue) return true;
                      if (!val || val === 0)
                        return "Timeout should not be zero";
                      if (val < 5 || val > 60)
                        return "The ring time is invalid. It must be a number between 5 and 60.";
                      return true;
                    },
                  })}
                  onKeyPress={(e) => {
                    if (!/[0-9]/.test(e.key)) {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                />
                <span className="seconds">{t("seconds")}</span>
                {getNestedError(errors, mergedFields.timeout) && (
                  <p className="text-danger">
                    {getNestedError(errors, mergedFields.timeout)}
                  </p>
                )}
              </div>
            </div>

            {/* ── Route on Timeout (inner autocomplete) ── */}
            <div className="mt-2 d-flex align-items-center justify-content-between w-100">
              <div className="tel-numbers fw-bold">{t("RouteonTimeout")}</div>
            </div>
            <InnerRouteCalls
              innerList={innerList}
              mergedFields={mergedFields}
              errors={errors}
              control={control}
              setValue={setValue}
              clearErrors={clearErrors}
              watch={watch}
              disabled={!timeoutFlagValue}
              routeDetails={routeDetails}
              command={command}
              isExternalSelected={isExternalSelected}
              acdFlag={acdFlag}
            />

            {/* ── Caller ID pass-through ── */}
            <div className="mt-2 d-flex align-items-center justify-content-between w-100">
              <label
                htmlFor={`cidPassFlag_${command}`}
                className="fw-bold"
              >
                Caller ID Pass Through
              </label>
              <MDBCheckbox
                name={`cidPassFlag_${command}`}
                id={`cidPassFlag_${command}`}
                checked={!!cidPassFlagValue}
                aria-label="Caller ID pass-through"
                onChange={onCidPassFlagChange}
              />
            </div>

            <div className="w-100">
              <Controller
                name={mergedFields.cidPass}
                control={control}
                rules={{
                  validate: (value) => {
                    // Only relevant when external number section is actually shown
                    if (!isExternalSelected) return true;
                    // If pass-through flag is ON, caller ID selection is not needed
                    if (cidPassFlagValue) return true;
                    // Otherwise a real caller ID must be chosen
                    if (!value || value === "-1")
                      return "Caller ID is required";
                    return validatePhoneNumber(value);
                  },
                }}
                render={({ field }) => (
                  <>
                    <MDBSelect
                      disabled={!!cidPassFlagValue}
                      value={watch(mergedFields.cidPass) ?? ""}
                      search
                      onChange={(e) => {
                        field.onChange(e?.value ?? "");
                        onCidPassChange(e);
                      }}
                      data={[
                        { value: "-1", text: "Choose Caller ID" },
                        ...callerIdList,
                      ]}
                    />
                    {getNestedError(errors, mergedFields.cidPass) && (
                      <p className="text-danger">
                        {getNestedError(errors, mergedFields.cidPass)}
                      </p>
                    )}
                  </>
                )}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ─── Inner timeout-routing autocomplete ──────────────────────────────────────
//
// Uses the INNER list (no External Numbers / Directory – same as
// OPERATOR_INNER_TYPES in BusyNoAnswerRoute).
// Validated via Controller so the error surfaces correctly.

const InnerRouteCalls = ({
  innerList,
  mergedFields,
  errors,
  control,
  setValue,
  clearErrors,
  watch,
  disabled,
  routeDetails,
  command,
  isExternalSelected,
  acdFlag = false,
}) => {
  const { t } = useTranslation();
  const [innerDisplayed, setInnerDisplayed] = useState([]);
  const [innerValue, setInnerValue] = useState("");
  const typingRef = useRef(null);

  const buildInnerDefault = useCallback(() => {
    return innerList.slice(0, MAX_DEFAULT_RESULTS).map((item) => ({
      ...item,
      _label: itemLabel(item),
    }));
  }, [innerList]);

  useEffect(() => {
    setInnerDisplayed(buildInnerDefault());
  }, [buildInnerDefault]);

  // Pre-populate from routeDetails
  useEffect(() => {
    if (!routeDetails?.timeout_routing) return;
    const tr = routeDetails.timeout_routing;
    if (typeof tr === "object" && tr !== null) {
      let catLabel = tr.category ?? "";
      if (!catLabel && acdFlag && ACD_TYPE_CATEGORY[tr.type]) {
        catLabel = ACD_TYPE_CATEGORY[tr.type];
      }
      const ext = tr.extension ? `${tr.extension} : ` : "";
      const cat = catLabel ? ` : ${catLabel}` : "";
      setInnerValue(`${ext}${tr.title ?? ""}${cat}`);
    }
  }, [routeDetails, acdFlag]);

  const onInnerSearch = useCallback(
    (raw) => {
      const term = (raw ?? "")
        .toLowerCase()
        .trim();
      if (!term) {
        setInnerDisplayed(buildInnerDefault());
        return;
      }
      const matched = innerList
        .filter(
          (item) =>
            item.title?.toLowerCase().includes(term) ||
            item.extensions?.toString().includes(term)
        )
        .slice(0, MAX_DEFAULT_RESULTS)
        .map((item) => ({ ...item, _label: itemLabel(item) }));

      setInnerDisplayed(matched);
    },
    [innerList, buildInnerDefault]
  );

  const onInnerSelect = useCallback(
    (selectedLabel) => {
      if (!selectedLabel) return;
      setInnerValue(selectedLabel);
      clearErrors(mergedFields.timeoutrouting);
      clearErrors(mergedFields.timeoutdesttyperouting);

      const item = innerList.find((i) => itemLabel(i) === selectedLabel);
      if (item) {
        // Inner (timeout) routing always uses non-ACD type resolution,
        // mirroring BusyNoAnswerRoute's OperatorSelect which passes null
        // for acdFlag on inner routes (innerRoute ? null : acdFlag).
        const destType = resolveDestinationType(item, false);
        const value = item.agentid ?? item.code ?? "";
        setValue(mergedFields.timeoutrouting, String(value), {
          shouldDirty: true,
        });
        setValue(mergedFields.timeoutdesttyperouting, destType, {
          shouldDirty: true,
        });
      }
    },
    [innerList, mergedFields, setValue, clearErrors]
  );

  useEffect(() => () => clearTimeout(typingRef.current), []);

  const timeoutFlagValue = watch(mergedFields.timeoutFlag);

  return (
    <div className="phoneselect mt-2 w-100">
      {/* Validated Controller on timeoutdesttyperouting – mirrors
          BusyNoAnswerRoute's OperatorSelect validation for the inner route */}
      <Controller
        name={mergedFields.timeoutdesttyperouting}
        control={control}
        rules={{
          validate: (value) => {
            // Only validate when the external section is actually shown
            if (!isExternalSelected) return true;
            // Only validate when timeout flag is ON
            if (!timeoutFlagValue) return true;
            if (!innerValue || innerValue === "")
              return "Please Select Where to Route on Timeout";
            return true;
          },
        }}
        render={() => (
          <>
            <MDBAutocomplete
              placeholder="Search and Select Timeout Destination"
              data={innerDisplayed}
              value={innerValue}
              disabled={disabled}
              displayValue={(item) => item._label ?? itemLabel(item)}
               onClick={(e) =>e.target.select()}
              onChange={(val) => {
                setInnerValue(val);
                if (val === "") {
                  clearErrors(mergedFields.timeoutrouting);
                  clearErrors(mergedFields.timeoutdesttyperouting);
                  setInnerDisplayed(buildInnerDefault());
                }
              }}
              onSelect={onInnerSelect}
              onSearch={onInnerSearch}
            />
            {getNestedError(errors, mergedFields.timeoutdesttyperouting) && (
              <p className="text-danger">
                {getNestedError(errors, mergedFields.timeoutdesttyperouting)}
              </p>
            )}
          </>
        )}
      />
    </div>
  );
};

export default RouteCalls;